import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HorizontalRule } from "@ucs/Components/HorizontalRule";
import { formatDateByYearMonthDay } from "@ucs/Helpers/Formatting";
import ChevronRight from "@ucs/Icons/ChevronRight";
import ChevronSmallRight from "@ucs/Icons/ChevronSmallRight";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        canViewWishLists: (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

let recentWishListsParameter = {
    page: 1,
    wishListLinesSort: "mostRecent",
    sort: "ModifiedOn DESC",
    expand: ["top3products", "favorite", "hidden", "tags"],
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsTableStyles {
    wishListCardGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    categoryImage?: LazyImageProps;
    dataTable: any;
    viewAllButton: any;
    containerName: any;
    topContainerTile: any;
    topHeadingContainer: any;
    topDataContainer: any;
    listNameLink: any;
    wishlistLineItem: any;
}

export const recentWishListsTableStyles: RecentWishListsTableStyles = {
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    topHeadingContainer: {
        css: css`
            display: inline-flex;
            flex-direction: row;
            width: 100%;
            margin-bottom: 8px !important;
        `,
    },
    topContainerTile: {
        css: css`
            background: #ffffff;
            padding: 10px;
            padding-left: 20px;
            padding-right: 20px;
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            margin-bottom: 8px;
        `,
    },
    containerName: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-start;
        `,
    },
    viewAllButton: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-end;
        `,
    },
    topDataContainer: {
        css: css``,
    },
    listNameLink: {
        css: css`
            color: ${UCSColors.green00};
            white-space: no-wrap;
        `,
    },
    wishlistLineItem: {
        css: css`
            gap: 10px;
        `,
    },
    dataTable: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: ${maxWidths[1]}px;
                        #listId {
                            order: 2 !important;
                        }
                        #previewId {
                            order: 1 !important;
                        }
                        #updatedId {
                            order: 3 !important;
                        }
                        #sharingId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[2]}px;
                        #listId {
                            order: 2 !important;
                        }
                        #previewId {
                            order: 1 !important;
                        }
                        #updatedId {
                            order: 3 !important;
                        }
                        #sharingId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[3]}px;
                    `,
                    css`
                        max-width: ${maxWidths[4]}px;
                    `,
                    css`
                        max-width: ${maxWidths[5]}px;
                    `,
                ]);
            }}
        `,
        cell: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: center;
                color: ${UCSColors.gray03};
            `,
        },
        header: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: auto;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        headerContainer: {
            css: css`
                display: flex;
                flex-direction: row;
                width: 100%;
                gap: 40px;
            `,
        },
        row: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: row;
                gap: 40px;
                padding-top: 2px;
                padding-bottom: 2px;
            `,
        },
        previewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
                overflow: auto;
                p {
                    overflow-y: hidden;
                }
            `,
        },
        mobilePreviewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
                overflow: auto;
                p {
                    overflow-y: hidden;
                }
            `,
        },
        mobileCell: {
            css: css`
                display: flex;
                flex-direction: column;
                white-space: no-wrap;
                color: ${UCSColors.gray03};
            `,
        },
        mobileHeader: {
            css: css`
                display: flex;
                flex-direction: column;
                align-self: center;
                justify-content: flex-start;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        mobileHeaderContainer: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },

        mobileRow: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-self: start;
            `,
        },
        innerTwoGrid: {
            css: css`
                display: grid;
                grid-template-columns: [first] 86px [line2] auto;
                grid-auto-flow: column;
                align-items: start;
            `,
        },
    },
};

const styles = recentWishListsTableStyles;

class RecentWishListsTable extends React.Component<Props> {
    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    render() {
        if (!this.props.canViewWishLists) {
            return null;
        }
        const {
            recentWishListsDataView: { value: recentWishLists },
            myListsPageNavLink,
        } = this.props;
        const myListsPageUrl = myListsPageNavLink ? myListsPageNavLink.url : undefined;

        return (
            <StyledWrapper {...styles.topContainerTile}>
                <StyledWrapper {...styles.topHeadingContainer}>
                    <Typography size={28} {...styles.containerName}>
                        {translate("My Lists")}
                    </Typography>
                    <Link
                        href={myListsPageUrl}
                        target="_self"
                        icon={{
                            iconProps: {
                                color: `${UCSColors.green00}`,
                                size: 16,
                                src: ChevronRight,
                            },
                        }}
                    >
                        <Typography size={18} {...styles.viewAllButton}>
                            {translate("View all ")}
                        </Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper role="presentation" {...styles.dataTable}>
                    <Hidden below="md">
                        <HorizontalRule />
                        <StyledWrapper {...styles.dataTable.headerContainer}>
                            <StyledWrapper id="listId" {...styles.dataTable.header}>
                                <Typography size={13}>List Name</Typography>
                            </StyledWrapper>
                            {/* <StyledWrapper id="previewId" {...styles.dataTable.header}>
                                <Typography size={13}>Preview</Typography>
                            </StyledWrapper> */}
                            <StyledWrapper id="updatedId" {...styles.dataTable.header}>
                                <Typography size={13}>Updated</Typography>
                            </StyledWrapper>
                            <StyledWrapper id="sharingId" {...styles.dataTable.header}>
                                <Typography size={13}>Sharing</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <HorizontalRule />
                    </Hidden>
                    <Hidden above="sm">
                        <HorizontalRule />
                    </Hidden>
                    {recentWishLists && recentWishLists.length === 0 && (
                        <GridItem width={12}>
                            <Typography {...styles.noWishListsText}>
                                {siteMessage("WishLists_NoWishlistsFound")}
                            </Typography>
                        </GridItem>
                    )}
                    {recentWishLists &&
                        recentWishLists.map(wishlist => (
                            <StyledWrapper key={wishlist.id}>
                                <StyledWrapper role="row" {...styles.dataTable.row}>
                                    <Hidden below="md" {...styles.dataTable.row}>
                                        <StyledWrapper id="listId" {...styles.dataTable.cell}>
                                            <Link href={`${myListsPageUrl}/Details?id=${wishlist.id}`} target="_self">
                                                <Typography {...styles.listNameLink} size={18}>
                                                    {wishlist.name}
                                                </Typography>
                                            </Link>
                                        </StyledWrapper>
                                        {/* <StyledWrapper id="previewId" {...styles.dataTable.previewImages}>
                                            {wishlist.wishListLineCollection &&
                                                wishlist.wishListLineCollection.map(wishListItem => (
                                                    <StyledWrapper
                                                        {...styles.dataTable.wishlistLineItem}
                                                        key={wishListItem.id}
                                                    >
                                                        <Link href={wishListItem.productUri}>
                                                            <LazyImage
                                                                src={wishListItem.smallImagePath}
                                                                width="50px"
                                                                height="50px"
                                                                altText={wishListItem.altText}
                                                            />
                                                        </Link>
                                                    </StyledWrapper>
                                                ))}
                                        </StyledWrapper> */}
                                        <StyledWrapper id="updatedId" {...styles.dataTable.cell}>
                                            <Typography size={18}>
                                                {formatDateByYearMonthDay(new Date(wishlist.updatedOn))}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper id="sharingId" {...styles.dataTable.cell}>
                                            <Typography size={18}>{wishlist.shareOption}</Typography>
                                        </StyledWrapper>
                                    </Hidden>
                                    <Hidden above="sm" {...styles.dataTable.mobileRow}>
                                        <StyledWrapper id="listId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    List Name
                                                </Typography>
                                                <Link
                                                    href={`${myListsPageUrl}/Details?id=${wishlist.id}`}
                                                    target="_self"
                                                >
                                                    <Typography {...styles.listNameLink} size={14}>
                                                        {wishlist.name}
                                                    </Typography>
                                                </Link>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        {/* <StyledWrapper id="previewId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    {translate("Preview")}
                                                </Typography>
                                                <StyledWrapper {...styles.dataTable.mobilePreviewImages}>
                                                    {wishlist.wishListLineCollection &&
                                                        wishlist.wishListLineCollection.map(wishListItem => (
                                                            <StyledWrapper
                                                                {...styles.dataTable.wishlistLineItem}
                                                                key={wishListItem.id}
                                                            >
                                                                <Link href={wishListItem.productUri}>
                                                                    <LazyImage
                                                                        src={wishListItem.smallImagePath}
                                                                        width="50px"
                                                                        height="50px"
                                                                        altText={wishListItem.altText}
                                                                    />
                                                                </Link>
                                                            </StyledWrapper>
                                                        ))}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper> */}
                                        <StyledWrapper id="updatedId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    Updated
                                                </Typography>
                                                <Typography size={14}>
                                                    {formatDateByYearMonthDay(new Date(wishlist.updatedOn))}
                                                </Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper id="sharingId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    Sharing
                                                </Typography>
                                                <Typography size={14}>{wishlist.shareOption}</Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </Hidden>
                                </StyledWrapper>
                                <HorizontalRule />
                            </StyledWrapper>
                        ))}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishListsTable),
    definition: {
        group: "Common",
        icon: "List",

        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
